import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import type OsanoConsentObject from '@alltrails/types/OsanoConsentObject';
import logError from '../logError';

const hasMarketingConsent = (): boolean => {
  try {
    const atOsanoCookie = Cookies.get('at_osano');
    if (atOsanoCookie) {
      const parsed = JSON.parse(atOsanoCookie);
      const consentObj: OsanoConsentObject = parsed.consent;
      return consentObj?.MARKETING === 'ACCEPT';
    }
  } catch (error) {
    logError(new Error('Failed to parse at_osano cookie', { cause: error }));
  }
  return false;
};

const WEATHER_PIXEL_BASE_URL = 'https://trkn.us/pixel/conv';
const PPT_ID = '26500';

// Conversion Event Goal IDs
const PAGEVIEW_GID = '68766';
const REGISTRATION_GID = '68767';
const START_TRIAL_GID = '68768';
const PURCHASE_GID = '68769';

const PAGEVIEW_GOAL = 'pageview';
const REGISTRATION_GOAL = 'registration';
const START_TRIAL_GOAL = 'start_trial';
const PURCHASE_GOAL = 'purchase';

/**
 * Fires a tracking pixel by making a GET request to the specified URL.
 * Uses fetch() with keepalive flag to ensure the request completes even if
 * the page navigates away.
 *
 * @param url - The complete pixel URL with all query parameters
 *
 * @example
 * ```typescript
 * firePixel('https://example.com/pixel?userId=123&value=99.99');
 * ```
 */
function firePixel(url: string): void {
  if (typeof window === 'undefined' || !hasMarketingConsent()) {
    return;
  }

  // keepalive, so request still fires despite navigation
  fetch(url, {
    keepalive: true,
    mode: 'no-cors',
    credentials: 'omit'
  }).catch(() => {
    // Do nothing
  });
}

type WeatherPixelParams = {
  g: string; // Goal/event type (e.g., 'purchase', 'registration')
  gid: string; // Goal ID
};

const trackWeatherPixelEvent = (params: WeatherPixelParams) => {
  const queryParams = new URLSearchParams({ ppt: PPT_ID, ord: uuidv4(), g: params.g, gid: params.gid });

  const pixelUrl = `${WEATHER_PIXEL_BASE_URL}?${queryParams.toString()}`;
  firePixel(pixelUrl);
};

const trackWeatherPurchase = () => {
  const params: WeatherPixelParams = {
    g: PURCHASE_GOAL,
    gid: PURCHASE_GID
  };

  trackWeatherPixelEvent(params);
};

const trackWeatherRegistration = () => {
  const params: WeatherPixelParams = {
    g: REGISTRATION_GOAL,
    gid: REGISTRATION_GID
  };

  trackWeatherPixelEvent(params);
};

const trackWeatherStartTrial = () => {
  const params: WeatherPixelParams = {
    g: START_TRIAL_GOAL,
    gid: START_TRIAL_GID
  };

  trackWeatherPixelEvent(params);
};

const trackWeatherPageView = () => {
  const params: WeatherPixelParams = {
    g: PAGEVIEW_GOAL,
    gid: PAGEVIEW_GID
  };

  trackWeatherPixelEvent(params);
};

export { trackWeatherPageView, trackWeatherPurchase, trackWeatherRegistration, trackWeatherStartTrial };
